import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Users, FileText, Calendar, MapPin, LayoutDashboard, Radio, ClipboardList, MessageSquare, BarChart3, Clock, Car, CalendarDays, Clipboard, Bell, Gauge, Receipt, Package, Banknote, Settings, FileInput, ChevronDown, ChevronRight, Calculator, CarFront, Truck, ShoppingCart, FileBarChart, Lock, Wrench, Target, Briefcase, Star, Shield, ArrowLeft, RefreshCw, Inbox, PhoneCall, Share2, ExternalLink, Check, CircleDot, HardDrive, Sun, Moon, BellRing, BookOpen, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API, downloadPdf } from '@/utils/api';
import { AiChatbot } from '@/components/AiChatbot';
import { useVehicleReadyNotification } from '@/hooks/useVehicleReadyNotification';
import GlobalChat from '@/components/GlobalChat';
import { useTheme } from '@/contexts/ThemeContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { useTranslation } from 'react-i18next';
import { LanguageSelectorDark } from '@/components/LanguageSelector';

const AdminLayout = ({ children, auth, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const role = auth?.admin?.role || 'admin';
  const [planAccess, setPlanAccess] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [deletionRequestCount, setDeletionRequestCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const employeeId = auth?.admin?.id || auth?.admin?.employee_id;
  const { status: pushStatus, subscribe: pushSubscribe } = usePushSubscription(employeeId);

  const presentationUrl = `${window.location.origin}/presentation`;

  const handleShareLink = async () => {
    try {
      await navigator.clipboard.writeText(presentationUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      window.open(presentationUrl, '_blank');
    }
  };

  // Sound notification for vehicle ready - active on ALL admin/reception pages
  useVehicleReadyNotification();

  useEffect(() => {
    axios.get(`${API}/tenant/plan-access`).then(r => setPlanAccess(r.data)).catch(() => {});
    // Fetch pending leave count
    const fetchPending = () => {
      axios.get(`${API}/leaves`).then(r => {
        const pending = (r.data || []).filter(l => l.status === 'pending').length;
        setPendingCount(pending);
      }).catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    // Fetch pending deletion requests count (admin only)
    const fetchDeletionRequests = () => {
      if (role === 'admin') {
        axios.get(`${API}/deletion-requests/count`).then(r => {
          setDeletionRequestCount(r.data?.count || 0);
        }).catch(() => {});
      }
    };
    fetchDeletionRequests();
    const drInterval = setInterval(fetchDeletionRequests, 15000);
    return () => { clearInterval(interval); clearInterval(drInterval); };
  }, [role]);

  // Auto-subscribe to push notifications on first visit
  useEffect(() => {
    if (pushStatus === 'prompt' && employeeId) {
      pushSubscribe();
    }
  }, [pushStatus, employeeId, pushSubscribe]);

  const groupPaths = {
    vehicules: ['/admin/vehicles', '/admin/used-vehicles'],
    atelier: ['/admin/workshop', '/admin/workshop-planning', '/admin/commandes-besoins'],
    stock: ['/admin/inventory', '/admin/used-tires', '/admin/suppliers', '/admin/purchase-orders'],
    communication: ['/admin/whatsapp-templates', '/admin/satisfaction', '/admin/insights'],
    rh: ['/admin/employees', '/admin/timesheets', '/admin/timeclock', '/admin/leaves', '/admin/calendrier-absences'],
    comptabilite: ['/admin/comptabilite', '/admin/bank-reconciliation', '/admin/finance', '/admin/kpi', '/admin/general-expenses', '/admin/supplier-invoices', '/admin/export-comptable', '/admin/payslips'],
    parametres: ['/admin/settings', '/admin/whatsapp-settings', '/admin/objectives', '/admin/rapport-hebdo', '/admin/reports', '/admin/geolocalisation', '/admin/deletion-requests', '/admin/backup'],
  };

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    Object.entries(groupPaths).forEach(([key, paths]) => {
      if (paths.some(p => location.pathname.startsWith(p))) initial[key] = true;
    });
    return initial;
  });

  const toggleGroup = (key) => setOpenGroups(g => ({ ...g, [key]: !g[key] }));

  // access: 'all' = admin+reception+atelier, 'admin_reception' = admin+reception only, 'admin' = admin only
  const navItems = [
    { path: '/admin/reception-dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, access: 'all', feature: 'dashboard' },
    { path: '/admin/timbreuse', label: 'Timbreuse', icon: Clock, access: 'all' },
    { path: '/admin/agenda', label: t('nav.agenda'), icon: CalendarDays, access: 'admin_reception', feature: 'appointments' },
    { path: '/admin/work-orders', label: t('nav.work_orders'), icon: Clipboard, access: 'all', feature: 'work_orders' },
    { path: '/admin/clients', label: t('nav.clients'), icon: Users, access: 'admin_reception', feature: 'clients' },
    { path: '/admin/relances', label: t('nav.follow_ups'), icon: PhoneCall, access: 'admin_reception', feature: 'clients' },
    { path: '/admin/billing', label: t('nav.quotes_invoices'), icon: Banknote, access: 'admin_reception', feature: 'billing' },
    { type: 'separator', access: 'all' },
    { path: '/admin/vehicles', label: t('nav.vehicles'), icon: Car, access: 'admin_reception', feature: 'vehicles' },
    { path: '/admin/used-vehicles', label: t('nav.used_vehicles'), icon: CarFront, access: 'admin_reception', feature: 'used_vehicles' },
    { path: '/admin/marketplace', label: t('nav.marketplace'), icon: ShoppingCart, access: 'admin_reception', feature: 'dashboard' },
    {
      type: 'group', key: 'atelier', label: t('nav.workshop'), icon: Wrench, access: 'all', feature: 'work_orders',
      children: [
        { path: '/admin/workshop', label: t('nav.workshop_view'), icon: Gauge, feature: 'work_orders' },
        { path: '/admin/workshop-planning', label: t('nav.planning'), icon: CalendarDays, feature: 'work_orders' },
      ],
    },
    {
      type: 'group', key: 'stock', label: t('nav.stock'), icon: Package, access: 'admin_reception', feature: 'inventory',
      children: [
        { path: '/admin/inventory', label: t('nav.inventory'), icon: Package, feature: 'inventory' },
        { path: '/admin/used-tires', label: "Pneus d'occasion", icon: Package },
        { path: '/admin/suppliers', label: t('nav.suppliers'), icon: Truck, feature: 'suppliers' },
        { path: '/admin/purchase-orders', label: t('nav.orders'), icon: ShoppingCart, feature: 'suppliers' },
      ],
    },
    { type: 'separator', access: 'admin' },
    {
      type: 'group', key: 'rh', label: t('nav.hr_employees'), icon: Users, access: 'admin', feature: 'dashboard',
      children: [
        { path: '/admin/employees', label: t('nav.employees'), icon: Users, feature: 'dashboard' },
        { path: '/admin/timesheets', label: t('nav.timesheets'), icon: FileText, feature: 'timeclock' },
        { path: '/admin/timeclock', label: t('nav.timeclock_mgmt'), icon: Shield, feature: 'timeclock' },
        { path: '/admin/leaves', label: t('nav.leaves'), icon: Calendar, feature: 'dashboard' },
        { path: '/admin/payslips', label: t('nav.payslips'), icon: FileText, feature: 'billing' },
      ],
    },
    {
      type: 'group', key: 'comptabilite', label: t('nav.accounting_label'), icon: Calculator, access: 'admin', feature: 'billing',
      children: [
        { path: '/admin/comptabilite', label: t('nav.accounting'), icon: BookOpen, feature: 'billing' },
        { path: '/admin/supplier-invoices', label: t('nav.supplier_invoices'), icon: FileInput, feature: 'suppliers' },
        { path: '/admin/export-comptable', label: t('nav.accounting_export'), icon: FileBarChart, feature: 'billing' },
        { path: '/admin/finance', label: t('nav.financial_view'), icon: Receipt, feature: 'financial_dashboard' },
      ],
    },
    {
      type: 'group', key: 'parametres', label: t('nav.settings'), icon: Settings, access: 'admin', feature: 'dashboard',
      children: [
        { path: '/admin/settings', label: t('nav.company'), icon: Settings, feature: 'dashboard' },
        { path: '/admin/whatsapp-settings', label: t('nav.whatsapp'), icon: MessageSquare, feature: 'dashboard' },
        { path: '/admin/whatsapp-templates', label: t('nav.whatsapp_messages'), icon: MessageSquare, feature: 'dashboard' },
        { path: '/admin/satisfaction', label: t('nav.satisfaction'), icon: Star, feature: 'notifications' },
        { path: '/admin/rappels', label: t('nav.reminders'), icon: Bell, feature: 'clients' },
        { path: '/admin/reports', label: t('nav.reports'), icon: FileBarChart, feature: 'reports' },
        { path: '/admin/objectives', label: t('nav.objectives'), icon: Target, feature: 'dashboard' },
        { path: '/admin/backup', label: t('nav.backup'), icon: HardDrive, feature: 'dashboard' },
      ],
    },
  ];

  const sidebar = planAccess?.sidebar || {};
  const hasFeature = (feature) => !planAccess || sidebar[feature] !== false;

  const visibleItems = navItems.filter(item => {
    if (item.access === 'admin' && role !== 'admin') return false;
    if (item.access === 'admin_reception' && role === 'atelier') return false;
    if (item.type === 'group' && item.children) {
      item = { ...item, children: item.children.filter(c => hasFeature(c.feature)) };
      if (item.children.length === 0) return false;
    }
    return true;
  });

  const PLAN_LABELS = { trial: t('plans.trial'), starter: t('plans.starter'), business: t('plans.business'), enterprise: t('plans.enterprise'), enterprise_plus: t('plans.enterprise_plus') };
  const ROLE_LABELS = { admin: t('roles.admin'), reception: t('roles.reception'), atelier: t('roles.workshop'), vente: t('roles.sales'), reception_vente: t('roles.reception_sales') };

  // Role-based sidebar theme
  const ROLE_THEMES = {
    admin: {
      bg: 'bg-slate-900',
      border: 'border-slate-700',
      hover: 'hover:bg-slate-800',
      active: 'bg-slate-800',
      activeChild: 'bg-slate-800/60',
      childBorder: 'border-slate-700',
      text: 'text-slate-400',
      accent: 'from-blue-500 to-blue-700',
      mobileBg: 'dark:bg-slate-900/80',
    },
    reception: {
      bg: 'bg-emerald-950',
      border: 'border-emerald-800',
      hover: 'hover:bg-emerald-900',
      active: 'bg-emerald-900',
      activeChild: 'bg-emerald-900/60',
      childBorder: 'border-emerald-800',
      text: 'text-emerald-300',
      accent: 'from-emerald-500 to-emerald-700',
      mobileBg: 'dark:bg-emerald-950/80',
    },
    vente: {
      bg: 'bg-violet-950',
      border: 'border-violet-800',
      hover: 'hover:bg-violet-900',
      active: 'bg-violet-900',
      activeChild: 'bg-violet-900/60',
      childBorder: 'border-violet-800',
      text: 'text-violet-300',
      accent: 'from-violet-500 to-violet-700',
      mobileBg: 'dark:bg-violet-950/80',
    },
    reception_vente: {
      bg: 'bg-teal-950',
      border: 'border-teal-800',
      hover: 'hover:bg-teal-900',
      active: 'bg-teal-900',
      activeChild: 'bg-teal-900/60',
      childBorder: 'border-teal-800',
      text: 'text-teal-300',
      accent: 'from-teal-500 to-teal-700',
      mobileBg: 'dark:bg-teal-950/80',
    },
  };
  const th = ROLE_THEMES[role] || ROLE_THEMES.admin;

  const renderNavItem = (item, idx) => {
    if (item.type === 'separator') {
      return <div key={idx} className={`border-t ${th.border} my-3`} />;
    }
    if (item.type === 'group') {
      const isOpen = !!openGroups[item.key];
      const isChildActive = item.children.some(c => location.pathname === c.path);
      const Icon = item.icon;
      const Arrow = isOpen ? ChevronDown : ChevronRight;
      return (
        <div key={item.key}>
          <Button
            onClick={() => toggleGroup(item.key)}
            variant="ghost"
            className={`w-full justify-start text-sm text-white ${th.hover} ${isChildActive ? th.activeChild : ''}`}
            data-testid={`nav-group-${item.key}`}
          >
            <Icon className="mr-3 w-4 h-4" />
            <span className="flex-1 text-left">{item.label}</span>
            <Arrow className="w-3.5 h-3.5 text-slate-400" />
          </Button>
          {isOpen && (
            <div className={`ml-4 pl-3 border-l ${t.childBorder} space-y-0.5 mt-0.5`}>
              {item.children.map(child => {
                const CIcon = child.icon;
                const isActive = location.pathname === child.path;
                return (
                  <div key={child.path}>
                    <Button
                      onClick={() => {
                        const guideEndpoints = {
                          'download-guide-reception': { url: '/user-guide/download', name: 'Guide_Reception_Vortekio.pdf' },
                          'download-guide-vente': { url: '/user-guide/download-vente', name: 'Guide_Vente_Vortekio.pdf' },
                          'download-guide-atelier': { url: '/user-guide/download-atelier', name: 'Guide_Atelier_Vortekio.pdf' },
                          'download-guide-admin': { url: '/user-guide/download-admin', name: 'Guide_Responsable_Vortekio.pdf' },
                          'download-guide-transfert': { url: '/transfer-guide/pdf', name: 'Vortekio_Guide_Technique_Transfert.pdf' },
                          'download-audit': { url: '/transfer-guide/audit-pdf', name: 'Vortekio_Audit_Complet.pdf' },
                        };
                        const guide = guideEndpoints[child.action];
                        if (guide) {
                          downloadPdf(`${API}${guide.url}`, guide.name);
                        } else {
                          navigate(child.path);
                        }
                      }}
                      variant="ghost"
                      className={`w-full justify-start text-sm text-slate-300 ${th.hover} hover:text-white ${isActive ? `${th.active} text-white` : ''}`}
                      data-testid={`nav-${child.path.split('/').pop()}`}
                    >
                      <CIcon className="mr-3 w-3.5 h-3.5" />
                      {child.label}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const locked = planAccess && item.feature && !hasFeature(item.feature);
    if (locked) {
      return (
        <div key={item.path} className="relative group">
          <Button
            variant="ghost"
            className="w-full justify-start text-sm text-slate-500 cursor-not-allowed opacity-50"
            data-testid={`nav-${item.path.split('/').pop()}-locked`}
            disabled
          >
            <Icon className="mr-3 w-4 h-4" />
            {item.label}
            <Lock className="ml-auto w-3 h-3 text-slate-600" />
          </Button>
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block z-50 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs text-slate-300 whitespace-nowrap shadow-xl">
            Plan {PLAN_LABELS[planAccess?.plan] || planAccess?.plan} — {t('plans.upgrade_required')}
          </div>
        </div>
      );
    }
    return (
      <div key={item.path}>
        <Button
          onClick={() => navigate(item.path)}
          variant="ghost"
          className={`w-full justify-start text-sm text-white ${th.hover} ${isActive ? th.active : ''}`}
          data-testid={`nav-${item.path.split('/').pop()}`}
        >
          <Icon className="mr-3 w-4 h-4" />
          {item.label}
          {item.badge > 0 && (
            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{item.badge}</span>
          )}
        </Button>
      </div>
    );
  };

  const flatItems = visibleItems.flatMap(item =>
    item.type === 'group' ? item.children : item.type === 'separator' ? [] : [item]
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[hsl(222,47%,6%)] flex">
      {/* Sidebar */}
      <div className={`hidden md:flex md:w-64 md:flex-col ${th.bg} text-white h-screen sticky top-0`}>
        <div className={`p-5 border-b ${th.border}`}>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-tight text-white">vortek<span className="relative inline-block">ı<span className="absolute -top-[2px] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-red-500 rounded-full"></span></span>o<span className="text-red-500">.</span></span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <p className={`text-xs ${th.text}`}>{ROLE_LABELS[role] || 'Administration'}</p>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`p-1.5 rounded-lg ${th.hover} transition-colors`}
                data-testid="dark-mode-toggle"
                title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              >
                {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-400" />}
              </button>
              <button
                onClick={() => {
                  if (pushStatus !== 'subscribed') pushSubscribe();
                }}
                className={`relative p-1.5 rounded-lg ${th.hover} transition-colors`}
                data-testid="push-notification-toggle"
                title={pushStatus === 'subscribed' ? 'Notifications actives' : 'Activer les notifications'}
              >
                <BellRing className={`w-4 h-4 ${pushStatus === 'subscribed' ? 'text-emerald-400' : 'text-slate-400'}`} />
                {pushStatus === 'subscribed' && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full" />
                )}
              </button>
              <button
                onClick={() => navigate('/admin/leaves')}
                className={`relative p-1.5 rounded-lg ${th.hover} transition-colors`}
                data-testid="notification-bell"
              >
                <Bell className="w-4 h-4 text-slate-400" />
                {pendingCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleItems.map((item, idx) => renderNavItem(item, idx))}
        </nav>

        <div className={`p-4 border-t ${th.border}`}>
          <div className="mb-3">
            <p className="text-sm font-medium">{auth.admin.first_name} {auth.admin.last_name}</p>
            <p className="text-xs text-slate-400">{auth.admin.email}</p>
          </div>
          <div className="flex gap-2 mb-3">
            <Button
              onClick={handleShareLink}
              variant="outline"
              className={`flex-1 justify-start text-xs ${th.border} ${th.hover} ${linkCopied ? 'text-emerald-400 border-emerald-600' : 'text-white'}`}
              data-testid="share-vortekio-copy-btn"
            >
              {linkCopied ? <Check className="mr-2 w-3.5 h-3.5" /> : <Share2 className="mr-2 w-3.5 h-3.5" />}
              {linkCopied ? t('common.copied') : t('common.copy')}
            </Button>
            <Button
              onClick={() => window.open(presentationUrl, '_blank')}
              variant="outline"
              className={`text-white ${th.border} ${th.hover} px-3`}
              data-testid="share-vortekio-open-btn"
              title={t('common.open')}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            className={`w-full justify-start text-white ${th.border} ${th.hover}`}
            data-testid="admin-logout-button"
          >
            <LogOut className="mr-2 w-4 h-4" />
            {t('common.logout')}
          </Button>
          <LanguageSelectorDark className="mt-2 justify-center" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <div className="md:hidden glass-effect sticky top-0 z-10 border-b border-slate-200 dark:border-slate-700 dark:bg-slate-900/80">
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-base font-bold text-slate-800 dark:text-white">vortek<span className="relative inline-block">ı<span className="absolute -top-[2px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] bg-red-500 rounded-full"></span></span>o<span className="text-red-500">.</span></span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={toggleTheme} className="p-1.5 rounded-lg" data-testid="dark-mode-toggle-mobile">
                {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
              </button>
              <Button variant="outline" size="sm" onClick={onLogout} className="dark:border-slate-600 dark:text-white">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="px-4 pb-3 flex space-x-2 overflow-x-auto">
            {flatItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  size="sm"
                  variant={isActive ? 'default' : 'outline'}
                  className="whitespace-nowrap text-xs"
                >
                  <Icon className="mr-1 w-3 h-3" />
                  {item.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          {/* Top toolbar - Back (detail pages only) & Refresh */}
          {(() => {
            const segments = location.pathname.split('/').filter(Boolean);
            const isDetailPage = segments.length > 2;
            return (
              <div className={`sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center ${isDetailPage ? 'justify-between' : 'justify-end'}`}>
                {isDetailPage && (
                  <button
                    onClick={() => {
                      if (window.history.length > 2) navigate(-1);
                      else navigate('/admin/reception-dashboard');
                    }}
                    className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('common.back')}</span>
                  </button>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                  data-testid="refresh-button"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('common.refresh')}</span>
                </button>
              </div>
            );
          })()}
          {children}
        </div>
      </div>
      <GlobalChat auth={auth} />
      <AiChatbot />
    </div>
  );
};

export default AdminLayout;
